import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import addressApi from "api/landlord/address";
import { useConversationByContact } from "views/inbox/api/use-conversation-by-contact";
import type { Application, Applicant } from "types/application";
import type { MessagePopupTarget } from "../store";

// POST /portal/v1/applicants/:id/chat_contact is an idempotent find-or-create:
// it returns the applicant's CRM contact and only mints one (plus its contact
// methods) the first time. Modelled as a query because the pop-up needs the id
// before it can render anything, and repeat calls are two reads.
const fetchApplicantContactId = async (applicantId: number) => {
  const { data } = await instance.post<{ contact_id: number }>(
    `/portal/v1/applicants/${applicantId}/chat_contact`
  );
  return data.contact_id;
};

/**
 * Applicants who can hold a chat: real, started, and not cancelled — plus
 * whoever the pop-up is currently open for, so the header dropdown always names
 * the person on screen even if their own application never started.
 */
const chatEligibleApplicants = (
  application?: Application,
  selectedApplicantId?: number
): Applicant[] =>
  (application?.applicants ?? []).filter(
    (applicant) =>
      applicant.id === selectedApplicantId ||
      (!applicant.is_manual && Boolean(applicant.application_started_at))
  );

/**
 * Resolves what the pop-up needs to show a conversation: the CRM contact (minted
 * from the applicant if it was never linked), the conversation for that contact
 * (or a draft when there is none yet), and the application's other applicants
 * for the header dropdown.
 */
export const useMessagePopupConversation = (target: MessagePopupTarget) => {
  const [selectedApplicantId, setSelectedApplicantId] = useState<
    number | undefined
  >(target.applicantId);
  // Set when the first send of a draft creates the conversation, so the pop-up
  // switches to it without waiting for the by-contact query to refetch. Scoped
  // to the contact it was minted for: the header dropdown can swap applicants
  // without remounting, and an unscoped id would keep the previous applicant's
  // thread on screen (and take the next send) under the new applicant's name.
  const [promoted, setPromoted] = useState<
    { contactId: number; conversationId: string } | undefined
  >();

  // Same query key as the application page, so opening the pop-up there is free.
  const { data: application } = useQuery({
    queryKey: ["APPLICATION", String(target.applicationId)],
    queryFn: () => addressApi.getApplicationById(String(target.applicationId)),
    enabled: Boolean(target.applicationId)
  });

  const applicants = useMemo(
    () => chatEligibleApplicants(application, selectedApplicantId),
    [application, selectedApplicantId]
  );

  const selectedApplicant = useMemo(
    () => applicants.find((applicant) => applicant.id === selectedApplicantId),
    [applicants, selectedApplicantId]
  );

  // The applicant the header is showing wins over the id the trigger passed:
  // switching applicant switches conversation.
  const knownContactId =
    selectedApplicant?.crm_contact_id ??
    (selectedApplicantId === target.applicantId ? target.contactId : undefined);

  const { data: linkedContactId, isError: isLinkError } = useQuery({
    queryKey: ["APPLICANT_CHAT_CONTACT", selectedApplicantId],
    queryFn: () => fetchApplicantContactId(selectedApplicantId as number),
    enabled: !knownContactId && Boolean(selectedApplicantId),
    staleTime: Infinity,
    retry: false
  });

  const contactId = knownContactId ?? linkedContactId;

  const { data: conversation, isLoading: isConversationLoading } =
    useConversationByContact(contactId);

  const promotedConversationId =
    promoted && promoted.contactId === contactId
      ? promoted.conversationId
      : undefined;
  const conversationId =
    conversation?.conversation.id ?? promotedConversationId;

  // Nothing left to try: the applicant has no linkable contact (no email and no
  // phone → 422 from chat_contact), or the trigger passed neither id.
  const hasContactError = !contactId && (isLinkError || !selectedApplicantId);

  // A draft became a real conversation. Remember which contact it belongs to so
  // it cannot leak onto another applicant.
  const onConversationResolved = useCallback(
    (id: string) => {
      if (contactId === undefined) return;
      setPromoted({ contactId, conversationId: id });
    },
    [contactId]
  );

  return {
    contactId,
    hasContactError,
    conversationId,
    unreadCount: conversation?.conversation.unread_count ?? 0,
    isConversationLoading,
    applicants,
    selectedApplicant,
    selectedApplicantId,
    setSelectedApplicantId,
    onConversationResolved,
    applicationId: target.applicationId
  };
};
